import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, Loader2 } from 'lucide-react';

const PhotoLightbox = ({ photos, initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  // Fonction pour obtenir l'URL du thumbnail
  const getThumbnailUrl = useCallback((url) => {
    if (url.includes('/api/files/') && !url.includes('?')) {
      return `${url}?thumb=true`;
    }
    return url;
  }, []);

  // Précharger une image
  const preloadImage = useCallback((index) => {
    if (index < 0 || index >= photos.length || loadedImages.has(index)) return;
    
    const img = new Image();
    img.onload = () => {
      setLoadedImages(prev => new Set([...prev, index]));
    };
    img.src = photos[index];
  }, [photos, loadedImages]);

  // Précharger les images adjacentes
  useEffect(() => {
    // Précharger l'image actuelle en priorité
    preloadImage(currentIndex);
    
    // Précharger les 2 images suivantes et précédentes
    const timeout = setTimeout(() => {
      for (let i = 1; i <= 2; i++) {
        preloadImage(currentIndex + i);
        preloadImage(currentIndex - i);
      }
    }, 100);
    
    return () => clearTimeout(timeout);
  }, [currentIndex, preloadImage]);

  // Gérer le chargement de l'image actuelle
  useEffect(() => {
    setIsLoading(!loadedImages.has(currentIndex));
  }, [currentIndex, loadedImages]);

  useEffect(() => {
    // Désactiver le scroll du body quand le lightbox est ouvert
    document.body.style.overflow = 'hidden';
    
    // Gestion des touches clavier
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentIndex]);

  const handleNext = () => {
    if (currentIndex < photos.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsZoomed(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsZoomed(false);
    }
  };

  const handleDownload = async () => {
    try {
      const photoUrl = photos[currentIndex];
      
      let downloadUrl = photoUrl;
      
      if (!photoUrl.startsWith('http')) {
        downloadUrl = `${window.location.origin}${photoUrl}`;
      }
      
      if (downloadUrl.includes('/api/files/') && !downloadUrl.includes('/download')) {
        downloadUrl = downloadUrl.replace(/\/api\/files\/([^/]+)$/, '/api/files/$1/download');
      }
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `photo-${currentIndex + 1}.jpg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      window.open(photos[currentIndex], '_blank');
    }
  };

  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
  };

  const handleImageLoad = () => {
    setLoadedImages(prev => new Set([...prev, currentIndex]));
    setIsLoading(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-95"
      onClick={onClose}
    >
      {/* Header avec compteur et boutons */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10">
        {/* Compteur */}
        <div className="text-white text-lg font-semibold">
          {currentIndex + 1} / {photos.length}
        </div>
        
        {/* Boutons d'action */}
        <div className="flex items-center gap-2">
          {/* Zoom */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleZoom();
            }}
            className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-white transition"
            title={isZoomed ? "Dézoomer" : "Zoomer"}
          >
            {isZoomed ? <ZoomOut className="w-6 h-6" /> : <ZoomIn className="w-6 h-6" />}
          </button>
          
          {/* Télécharger */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-white transition"
            title="Télécharger"
          >
            <Download className="w-6 h-6" />
          </button>
          
          {/* Fermer */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-white transition"
            title="Fermer (Échap)"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Navigation précédent */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrevious();
          }}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition z-10"
          title="Photo précédente (←)"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      {/* Image principale avec loader élégant */}
      <div
        className="relative max-w-7xl max-h-[90vh] mx-4 flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        style={{ minWidth: '300px', minHeight: '300px' }}
      >
        {/* Loader élégant centré */}
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-black bg-opacity-50 rounded-lg">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-white border-opacity-20 rounded-full"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-white text-sm mt-4 opacity-70">Chargement...</p>
          </div>
        )}
        
        {/* Image avec fade-in */}
        <img
          src={photos[currentIndex]}
          alt={`Photo ${currentIndex + 1}`}
          className={`max-w-full max-h-[90vh] object-contain transition-all duration-500 ${
            isZoomed ? 'scale-150 cursor-zoom-out' : 'scale-100 cursor-zoom-in'
          } ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onClick={toggleZoom}
          onLoad={handleImageLoad}
          style={{
            maxHeight: '90vh',
            width: 'auto',
            height: 'auto'
          }}
        />
      </div>

      {/* Navigation suivant */}
      {currentIndex < photos.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition z-10"
          title="Photo suivante (→)"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Miniatures en bas - utilise les thumbnails */}
      <div className="absolute bottom-0 left-0 right-0 p-4 overflow-x-auto">
        <div className="flex gap-2 justify-center">
          {photos.map((photo, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
                setIsZoomed(false);
              }}
              className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition ${
                index === currentIndex
                  ? 'border-white scale-110'
                  : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img
                src={getThumbnailUrl(photo)}
                alt={`Miniature ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Images préchargées (cachées) pour les 2 suivantes/précédentes */}
      <div className="hidden">
        {[-2, -1, 1, 2].map(offset => {
          const idx = currentIndex + offset;
          if (idx >= 0 && idx < photos.length && !loadedImages.has(idx)) {
            return (
              <img 
                key={idx} 
                src={photos[idx]} 
                alt="" 
                onLoad={() => setLoadedImages(prev => new Set([...prev, idx]))}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default PhotoLightbox;
